import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { createLogger } from '../utils/logger.js';

const log = createLogger('Analyze');
import AnalysisDisplay from '../components/AnalysisDisplay.jsx';
import {
    ExecutiveSummaryOverview,
    ComprehensiveResearchSection,
    ValidationResultsSection,
    ConfidenceDashboardSection,
    CostAnalysisSection,
    ProgressiveStackEvolution
} from '../components/AnalysisDisplay.jsx';
import TechnicalArchitecture from '../components/TechnicalArchitecture.jsx';
import EnhancedAnalysisLoader from '../components/EnhancedAnalysisLoader.jsx';
import CreditTracker from '../components/CreditTracker.jsx';
import AnalyzePromptGenerator from '../components/AnalyzePromptGenerator.jsx';
import SessionManager from '../components/SessionManager.jsx';
import AppFlowDiagram from '../components/AppFlowDiagram.jsx';
import InsufficientCreditsAlert from '../components/InsufficientCreditsAlert.jsx';
import ExportHTMLButton from '../components/ExportHTMLButton.jsx';
import CloudflareDeployWizard from '../components/CloudflareDeployWizard.jsx';
import { useArchitectApp } from '../hooks/useArchitectApp.jsx';
import {
    LogoText,
    Button,
    Text,
    Heading,
    Container,
    NavLink,
    Badge,
    Icon,
    MobileBottomNav
} from '../components/ui/index.jsx';

export default function Analyze() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const flowStarted = useRef(false);
    const currentSessionRef = useRef(null);
    const initRan = useRef(false); // Prevent multiple initializations

    // Use the comprehensive app hook that contains all migrated logic
    const {
        // State
        v2Data,
        currentStep,
        isLoading,
        loadingMessage,
        error,
        activeTab,
        statusTags,
        stepRetryInfo,
        showRetryOption,
        sessionId,
        promptGenerationProgress,

        // Actions
        setSessionId,
        loadSession,
        resumeSession,
        startV2Flow,
        switchToTab,
        clearError,
        showError,
        retryFromStep,

        // 🔄 Queue system
        queueStatus,
        cancelQueue
    } = useArchitectApp();

    // Cloudflare deploy status
    const [deployStatus, setDeployStatus] = useState(null);
    const [isDeploying, setIsDeploying] = useState(false);

    useEffect(() => {
        const initializeAnalysis = async () => {
            try {
                // Get session ID from URL or storage (SAME AS ORIGINAL app.js)
                const sessionFromUrl = searchParams.get('session');
                const sessionFromStorage = sessionStorage.getItem('currentSessionId');
                const currentSessionId = sessionFromUrl || sessionFromStorage;


                if (!currentSessionId) {
                    showError('No session found. Please start from the home page.');
                    return;
                }

                // Simple one-time initialization check per session
                if (initRan.current && currentSessionRef.current === currentSessionId) {
                    return;
                }

                // Reset for new session
                if (currentSessionRef.current !== currentSessionId) {
                    initRan.current = false;
                    flowStarted.current = false;
                }

                initRan.current = true;

                // Set session in the hook
                setSessionId(currentSessionId);
                currentSessionRef.current = currentSessionId;

                // 🔒 SECURITY: ProtectedRoute ensures user is authenticated
                // This page handles session-specific operations
                const skipInit = searchParams.get('skipinit') === '1';
                log.log('skipInit:', skipInit, 'sessionId:', currentSessionId);

                if (!skipInit) {
                    log.log('Starting new V2 flow');
                    flowStarted.current = true;
                    await startV2Flow();
                } else {
                    // Resume session - handles both cancelled workflows and completed sessions
                    // resumeSession() will reconnect to Agent WebSocket if workflow is incomplete
                    // Falls back to loadSession() if workflow is already completed
                    log.log('Resuming session with skipinit=1');
                    await resumeSession(currentSessionId);
                    log.log('Session resumed successfully');
                }

                // If a specific tab is requested via URL, switch to it
                const requestedTab = searchParams.get('tab');
                const allowedTabs = new Set(['overview', 'research', 'technical', 'flowdiagram', 'prompts', 'questions', 'deploy']);
                if (requestedTab && allowedTabs.has(requestedTab)) {
                    switchToTab(requestedTab);
                }

            } catch (error) {
                log.error('ERROR in initializeAnalysis:', error);
                // 🔒 SECURITY: Fail secure on any error
                // - 401/403 = session expired or unauthorized access
                // - Other errors = redirect to prevent information leakage
                const isSecurityError = error?.status === 401 ||
                    error?.status === 403 ||
                    error?.message?.includes('401') ||
                    error?.message?.includes('403') ||
                    error?.message?.includes('Unauthorized');

                log.log('isSecurityError:', isSecurityError);
                if (isSecurityError) {
                    log.log('Redirecting to / due to security error');
                    navigate('/');
                    return;
                }

                // Legitimate errors (network issues, etc.) - show error to authenticated user
                showError('Failed to load analysis. Please try again.');
                flowStarted.current = false;
                currentSessionRef.current = null;
            }
        };

        initializeAnalysis();

        // Cleanup function (simplified - no more question flow handlers)
        return () => {
            // No cleanup needed for question flow anymore
        };
    }, [searchParams]); // Only depend on URL changes to prevent infinite loops


    const handleTabSwitch = (tabName) => {
        switchToTab(tabName);
    };

    // Error state
    if (error && !v2Data.analysis) {
        return (
            <div className="min-h-screen bg-black flex flex-col">
                {/* Header */}
                <header className="border-b border-white/5">
                    <Container size="6xl">
                        <div className="flex justify-between items-center py-4">
                            <div className="flex items-center">
                                <LogoText size="lg" />
                            </div>
                            <nav className="hidden md:flex space-x-8">
                                <Button variant="ghost" onClick={() => navigate('/')}>
                                    Back to Home
                                </Button>
                            </nav>
                        </div>
                    </Container>
                </header>

                {/* Error Content */}
                <main className="flex-1">
                    <Container size="6xl">
                        <section className="pt-16 pb-8 text-center">
                            {/* Check if it's an insufficient credits error */}
                            {error && error.type === 'insufficient_credits' ? (
                                <InsufficientCreditsAlert
                                    error={error}
                                    onClose={() => {
                                        clearError();
                                        navigate('/');
                                    }}
                                    className="max-w-2xl mx-auto"
                                />
                            ) : (
                                <div className="glass-panel p-6 md:p-12 rounded-2xl">
                                    <Icon name="warning" size="2xl" className="mb-6 mx-auto text-[#FF6B6B]" />
                                    <Heading level={2} className="mb-4 text-white">Analysis Error</Heading>
                                    <Text variant="secondary" className="mb-8 max-w-md mx-auto text-gray-300">
                                        {typeof error === 'string' ? error : error?.message || 'An error occurred'}
                                    </Text>
                                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                        <Button
                                            onClick={() => {
                                                // Reset flow flags to allow retry
                                                flowStarted.current = false;
                                                currentSessionRef.current = null;
                                                clearError();
                                                startV2Flow();
                                            }}
                                            variant="primary"
                                        >
                                            Try Again
                                        </Button>
                                        <Button
                                            onClick={() => navigate('/')}
                                            variant="secondary"
                                        >
                                            Back to Home
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </section>
                    </Container>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black text-white selection:bg-[#7B61FF] selection:text-white relative overflow-x-hidden">
            {/* Aurora Background */}
            <div className="fixed inset-0 aurora-bg z-0 pointer-events-none"></div>

            {/* Ambient Orbs */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-[#7B61FF]/20 rounded-full blur-[100px] animate-pulse-slow"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-[#9F7AEA]/20 rounded-full blur-[100px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
            </div>

            {/* Content Wrapper */}
            <div className="relative z-10 flex flex-col min-h-screen">
                {/* Header */}
                <header className="sticky top-0 z-50 border-b border-white/5 bg-black/50 backdrop-blur-md">
                    <Container size="6xl">
                        <div className="flex justify-between items-center py-4">
                            <div className="flex items-center">
                                <Button onClick={() => navigate('/')} variant="ghost" className="flex items-center hover:bg-white/5 rounded-xl transition-all duration-300 group">
                                    <LogoText size="lg" />
                                </Button>
                            </div>
                            <nav className="flex items-center space-x-4 md:space-x-8">
                                {/* Status indicators - mobile friendly */}
                                <div className="hidden sm:flex items-center space-x-3">
                                    {statusTags.analyzing && (
                                        <div className="flex items-center px-3 py-1 rounded-full bg-[#3B82F6]/10 border border-[#3B82F6]/20 text-[#3B82F6] text-xs font-medium animate-pulse">
                                            <span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] mr-2"></span>
                                            Analyzing...
                                        </div>
                                    )}
                                    {statusTags.analysisComplete && (
                                        <div className="flex items-center px-3 py-1 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-medium">
                                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-2"></span>
                                            Complete
                                        </div>
                                    )}
                                    {statusTags.platform && (
                                        <div className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-gray-300 text-xs font-medium">
                                            {statusTags.platform}
                                        </div>
                                    )}
                                    {statusTags.promptsComplete && (
                                        <div className="flex items-center px-3 py-1 rounded-full bg-[#7B61FF]/10 border border-[#7B61FF]/20 text-[#BA87FF] text-xs font-medium">
                                            <span className="w-1.5 h-1.5 rounded-full bg-[#7B61FF] mr-2"></span>
                                            Prompts Ready
                                        </div>
                                    )}
                                </div>
                                <Button
                                    onClick={() => navigate('/')}
                                    variant="ghost"
                                    size="sm"
                                    className="flex items-center text-gray-400 hover:text-white hover:bg-white/5 transition-all text-sm"
                                >
                                    <Icon name="arrowLeft" size="xs" className="mr-2" />
                                    Back to Home
                                </Button>
                            </nav>
                        </div>
                    </Container>
                </header>

                {/* Mobile status indicators */}
                {(statusTags.analyzing || statusTags.analysisComplete || statusTags.platform || statusTags.promptsComplete) && (
                    <div className="sm:hidden bg-black/40 backdrop-blur-sm border-b border-white/5">
                        <div className="max-w-6xl mx-auto px-4 py-3">
                            <div className="flex flex-wrap items-center gap-2">
                                {statusTags.analyzing && (
                                    <Badge variant="info" size="xs">Analyzing...</Badge>
                                )}
                                {statusTags.analysisComplete && (
                                    <Badge variant="success" size="xs">Complete</Badge>
                                )}
                                {statusTags.platform && (
                                    <Badge variant="secondary" size="xs">{statusTags.platform}</Badge>
                                )}
                                {statusTags.promptsComplete && (
                                    <Badge variant="info" size="xs">Prompts Ready</Badge>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Main Content */}
                <main className="flex-1 w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 pb-32 md:pb-12">
                    {/* Session Management */}
                    {(!isLoading && currentStep > 1) && (
                        <section className="mb-8">
                            <SessionManager
                                currentSessionData={{
                                    sessionId,
                                    originalIdea: v2Data.originalIdea,
                                    platform: v2Data.platform,
                                    research: v2Data.research,
                                    validation: v2Data.validation,
                                    answers: v2Data.answers,
                                    analysis: v2Data.analysis,
                                    prompts: v2Data.prompts,
                                    flowDiagram: v2Data.flowDiagram,
                                    currentStep,
                                    createdAt: new Date().toISOString()
                                }}
                                showSaveButton={true}
                                showLoadButton={true}
                                onLoadSession={(sessionData) => {
                                    if (sessionData.sessionData && sessionData.sessionId) {
                                        loadSession(sessionData.sessionId);
                                    }
                                }}
                                onSaveComplete={(savedSession) => {
                                }}
                                className="max-w-4xl mx-auto"
                            />
                        </section>
                    )}

                    {/* 🔄 Queue Status UI */}
                    {queueStatus && (
                        <section className="animate-fade-in max-w-2xl mx-auto">
                            <div className="bg-amber-900/20 border border-amber-500/30 rounded-2xl p-8 text-center backdrop-blur-sm">
                                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-500/20 flex items-center justify-center">
                                    <svg className="w-8 h-8 text-amber-400 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <Heading level={3} className="text-amber-300 mb-2">
                                    You're #{queueStatus.position} in queue
                                </Heading>
                                <Text className="text-amber-200/80 mb-1">
                                    Estimated wait: ~{queueStatus.estimatedWait} minutes
                                </Text>
                                {queueStatus.waitTimeBasedOn?.sampleSize > 0 && (
                                    <Text className="text-xs text-amber-200/40 mb-3">
                                        Based on {queueStatus.waitTimeBasedOn.sampleSize} recent workflows
                                    </Text>
                                )}

                                {/* 🎯 TPM TRACKING: Show real-time API capacity */}
                                {queueStatus.tpm && (
                                    <div className="mt-3 mb-4 p-3 bg-amber-900/30 rounded-lg">
                                        <div className="flex justify-between items-center mb-2">
                                            <Text className="text-xs text-amber-200/60">API Capacity</Text>
                                            <Text className="text-xs text-amber-200/40">
                                                {queueStatus.tpm.dataSource === 'api_response' ? 'Live' : 'Estimated'}
                                            </Text>
                                        </div>
                                        <div className="h-2 bg-amber-900/50 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all duration-500"
                                                style={{ width: `${queueStatus.tpm.outputTokens?.percentAvailable || 0}%` }}
                                            />
                                        </div>
                                        <Text className="text-xs text-amber-200/40 mt-1 text-center">
                                            {queueStatus.tpm.outputTokens?.percentAvailable || 0}% tokens available
                                        </Text>
                                    </div>
                                )}

                                <Text className="text-sm text-amber-200/60 mb-6">
                                    We'll start automatically when a slot opens.
                                    Keep this page open to see progress.
                                </Text>
                                <Button
                                    variant="outline"
                                    onClick={cancelQueue}
                                    className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                                >
                                    Cancel
                                </Button>
                            </div>
                        </section>
                    )}

                    {/* Enhanced Loading State */}
                    {isLoading && !queueStatus && (
                        <EnhancedAnalysisLoader
                            currentStep={currentStep}
                            loadingMessage={loadingMessage}
                            platform={v2Data.platform}
                            originalIdea={v2Data.originalIdea}
                            isLoading={isLoading}
                            stepTimings={v2Data.stepTimings}
                        />
                    )}

                    {/* Analysis Content */}
                    {!isLoading && (v2Data.analysis || activeTab === 'questions') && (
                        <section className="animate-fade-in space-y-8">
                            {/* Tab Navigation - Desktop */}
                            <div className="hidden md:flex justify-center mb-10">
                                <div className="glass-panel p-1.5 rounded-full inline-flex items-center space-x-1 border border-white/10 bg-black/40 backdrop-blur-lg">
                                    {[
                                        { id: 'overview', icon: 'chart', label: 'Overview' },
                                        { id: 'research', icon: 'refresh', label: 'Evolution' },
                                        { id: 'technical', icon: 'settings', label: 'Technical' },
                                        { id: 'flowdiagram', icon: 'clipboard', label: 'App Flow' },
                                        { id: 'prompts', icon: 'rocket', label: 'AI Prompts' }
                                    ].map((tab) => (
                                        <button
                                            key={tab.id}
                                            onClick={() => handleTabSwitch(tab.id)}
                                            className={`px-6 py-2.5 rounded-full flex items-center text-sm font-medium transition-all duration-300 ${activeTab === tab.id
                                                ? 'bg-[#7B61FF] text-white shadow-[0_0_20px_rgba(123,97,255,0.4)]'
                                                : 'text-gray-400 hover:text-white hover:bg-white/5'
                                                }`}
                                        >
                                            <Icon name={tab.icon} size="xs" className="mr-2" />
                                            {tab.label}
                                        </button>
                                    ))}
                                    {/* Deploy Tab - Cloudflare only, bigger & prominent */}
                                    {v2Data.platform === 'cloudflare' && (
                                        <button
                                            onClick={() => handleTabSwitch('deploy')}
                                            className={`ml-2 px-8 py-3 rounded-full flex items-center text-base font-bold transition-all duration-300 ${activeTab === 'deploy'
                                                ? 'bg-gradient-to-r from-[#F6821F] to-[#FAAD3F] text-white shadow-[0_0_25px_rgba(246,130,31,0.5)]'
                                                : 'bg-[#F6821F]/20 text-[#F6821F] border border-[#F6821F]/40 hover:bg-[#F6821F]/30'
                                                }`}
                                        >
                                            <Icon name="lightning" size="sm" className="mr-2" />
                                            Deploy
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Tab Content Area */}
                            <div className="relative min-h-[500px]">
                                {activeTab === 'overview' && (
                                    <div className="animate-fade-in">
                                        <ExecutiveSummaryOverview
                                            data={v2Data.analysis}
                                            research={v2Data.research}
                                            validation={v2Data.validation}
                                            platform={v2Data.platform}
                                            originalIdea={v2Data.originalIdea}
                                            answers={v2Data.answers}
                                            onNavigateToTab={handleTabSwitch}
                                        />
                                    </div>
                                )}

                                {activeTab === 'research' && (
                                    <div className="space-y-8 animate-fade-in">
                                        <div className="text-center mb-8">
                                            <Heading level={2} className="text-3xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-b from-white to-white/60">
                                                Research & Architecture Evolution
                                            </Heading>
                                            <Text variant="secondary" className="max-w-2xl mx-auto">
                                                Follow the AI's research process from initial discovery to final optimized architecture
                                            </Text>
                                        </div>

                                        {(v2Data.research || v2Data.validation) && (
                                            <ProgressiveStackEvolution
                                                research={v2Data.research}
                                                validation={v2Data.validation}
                                                finalStack={undefined}
                                            />
                                        )}

                                        {v2Data.research && (
                                            <ComprehensiveResearchSection research={v2Data.research} />
                                        )}

                                        {v2Data.validation && (
                                            <ValidationResultsSection validation={v2Data.validation} />
                                        )}

                                        {v2Data.analysis?.metadata && (
                                            <ConfidenceDashboardSection metadata={v2Data.analysis.metadata} />
                                        )}

                                        {v2Data.research?.estimatedCost && (
                                            <CostAnalysisSection costData={v2Data.research.estimatedCost} research={v2Data.research} />
                                        )}
                                    </div>
                                )}

                                {activeTab === 'technical' && (
                                    <div className="animate-fade-in">
                                        <TechnicalArchitecture
                                            analysisResponse={v2Data.analysis}
                                            research={v2Data.research}
                                            validation={v2Data.validation}
                                            platform={v2Data.platform}
                                        />
                                    </div>
                                )}

                                {activeTab === 'flowdiagram' && (
                                    <div className="animate-fade-in">
                                        <AppFlowDiagram
                                            analysisData={v2Data.analysis}
                                            diagramData={v2Data.flowDiagram?.data || v2Data.flowDiagram}
                                        />
                                    </div>
                                )}

                                {activeTab === 'prompts' && (
                                    <div className="animate-fade-in">
                                        {(() => {
                                            // Fix data structure
                                            let promptData;
                                            if (v2Data.prompts) {
                                                if (Array.isArray(v2Data.prompts.microPrompts) && v2Data.prompts.microPrompts.length > 0) {
                                                    promptData = {
                                                        microPrompts: v2Data.prompts.microPrompts,
                                                        metadata: v2Data.prompts.metadata || {},
                                                        platform: v2Data.prompts.platform || v2Data.platform,
                                                        confidence: (typeof v2Data.prompts?.metrics?.overallConfidence === 'number'
                                                            ? v2Data.prompts.metrics.overallConfidence
                                                            : (typeof v2Data.prompts?.metadata?.claudeConfidence === 'number'
                                                                ? v2Data.prompts.metadata.claudeConfidence
                                                                : (v2Data.analysis?.confidence
                                                                    || v2Data.analysis?.metadata?.claudeConfidence
                                                                    || v2Data.prompts.confidence)))
                                                    };
                                                }
                                                else if (v2Data.prompts.data?.prompts) {
                                                    promptData = {
                                                        prompts: v2Data.prompts.data.prompts,
                                                        platform: v2Data.platform,
                                                        confidence: (typeof v2Data.prompts?.metrics?.overallConfidence === 'number'
                                                            ? v2Data.prompts.metrics.overallConfidence
                                                            : (typeof v2Data.prompts?.metadata?.claudeConfidence === 'number'
                                                                ? v2Data.prompts.metadata.claudeConfidence
                                                                : (v2Data.analysis?.confidence
                                                                    || v2Data.analysis?.metadata?.claudeConfidence
                                                                    || v2Data.prompts.confidence || 0.85))),
                                                        metadata: v2Data.prompts.data.metadata || {
                                                            totalEstimatedTime: v2Data.prompts.data.metadata?.totalEstimatedTime,
                                                            recommendedOrder: Object.keys(v2Data.prompts.data.prompts),
                                                            criticalPath: Object.keys(v2Data.prompts.data.prompts).slice(0, 3)
                                                        }
                                                    };
                                                }
                                                else if (typeof v2Data.prompts === 'object' && Object.values(v2Data.prompts).some(p => p.title && p.prompt)) {
                                                    promptData = {
                                                        prompts: v2Data.prompts,
                                                        platform: v2Data.platform,
                                                        confidence: (typeof v2Data.prompts?.metrics?.overallConfidence === 'number'
                                                            ? v2Data.prompts.metrics.overallConfidence
                                                            : (typeof v2Data.prompts?.metadata?.claudeConfidence === 'number'
                                                                ? v2Data.prompts.metadata.claudeConfidence
                                                                : (v2Data.analysis?.confidence
                                                                    || v2Data.analysis?.metadata?.claudeConfidence
                                                                    || 0.85))),
                                                        metadata: {
                                                            totalEstimatedTime: v2Data.prompts?.metadata?.totalEstimatedTime,
                                                            recommendedOrder: Object.keys(v2Data.prompts),
                                                            criticalPath: Object.keys(v2Data.prompts).slice(0, 3)
                                                        }
                                                    };
                                                }
                                                else {
                                                    promptData = {
                                                        prompts: v2Data.prompts,
                                                        platform: v2Data.platform,
                                                        confidence: (typeof v2Data.prompts?.metrics?.overallConfidence === 'number'
                                                            ? v2Data.prompts.metrics.overallConfidence
                                                            : (typeof v2Data.prompts?.metadata?.claudeConfidence === 'number'
                                                                ? v2Data.prompts.metadata.claudeConfidence
                                                                : (v2Data.analysis?.confidence
                                                                    || v2Data.analysis?.metadata?.claudeConfidence
                                                                    || 0.85))),
                                                        metadata: {}
                                                    };
                                                }
                                            } else {
                                                promptData = null;
                                            }

                                            if (!promptData || (!promptData.prompts && !promptData.microPrompts)) {
                                                return (
                                                    <div className="glass-panel p-12 text-center rounded-2xl border border-white/5">
                                                        <div className="bg-[#7B61FF]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                                            <Icon name="clipboard" size="2xl" className="text-[#7B61FF]" />
                                                        </div>
                                                        <Text variant="primary" size="lg" weight="medium" className="mb-2">No prompts available</Text>
                                                        <Text variant="muted" size="sm">Run analysis to generate prompts</Text>
                                                    </div>
                                                );
                                            }

                                            return (
                                                <AnalyzePromptGenerator
                                                    promptData={promptData}
                                                    batchProgress={promptGenerationProgress}
                                                    onError={(error) => {
                                                    }}
                                                    onRetry={() => {
                                                    }}
                                                />
                                            );
                                        })()}
                                    </div>
                                )}

                                {/* Deploy Tab - Cloudflare only */}
                                {activeTab === 'deploy' && v2Data.platform === 'cloudflare' && (
                                    <div className="animate-fade-in">
                                        <div className="max-w-3xl mx-auto p-6 md:p-8 rounded-2xl border border-[#F6821F]/30 bg-gradient-to-br from-[#F6821F]/5 to-[#FAAD3F]/5 backdrop-blur-sm">
                                            <div className="flex items-center gap-3 mb-6">
                                                <div className="w-12 h-12 rounded-xl bg-[#F6821F]/20 flex items-center justify-center">
                                                    <svg className="w-7 h-7 text-[#F6821F]" viewBox="0 0 24 24" fill="currentColor">
                                                        <path d="M16.5088 16.8447c.1475-.5068.0908-.9707-.1553-1.3154-.2246-.3164-.6045-.499-1.0615-.5205l-8.6592-.1123a.1559.1559 0 01-.1333-.0713c-.0283-.0434-.0351-.0986-.0205-.1553.0283-.0986.1182-.1709.2168-.1709l8.7275.1123c.9424.0049 1.9629-.5547 2.3838-1.3252l.5762-1.0557c.0147-.0283.0244-.0615.0283-.0957.0049-.0293.0098-.0596.0098-.0889 0-.4629-.3926-.8388-.876-.8388H4.5874c-.0371 0-.0713.0215-.0898.0547L2.4823 15.3155c-.7383 1.3555-.7822 2.7227-.0938 3.8203.6826 1.086 1.9727 1.7324 3.4463 1.7324h10.9805c.4346 0 .8506-.3311.9463-.7559l.7471-3.2676zM19.7539 8.084c-.2148-2.8672-2.5273-5.1797-5.418-5.4121-.6123-.0488-1.2031.0068-1.7558.1406-.2275.0547-.3135.3232-.1758.5166.5742.8047.9102 1.7812.9102 2.8418 0 .2031-.0107.4023-.0313.5986-.0205.1992.1426.3711.3398.3867.0547.0049.1094.0059.1641.0059 1.4355 0 2.6113 1.126 2.6709 2.5596.0137.3291.2871.5908.6162.6016l.7305.0176c1.2617.0312 2.4004.8848 2.6807 2.1172.2383 1.0479-.1035 2.1289-.8916 2.8125l.8486.0049c1.0967-.5742 1.8994-1.6113 2.0879-2.8916.2246-1.5166-.4443-3.0127-1.7764-3.9999z"/>
                                                    </svg>
                                                </div>
                                                <div>
                                                    <h3 className="text-xl font-bold text-white">Deploy to Cloudflare</h3>
                                                    <p className="text-sm text-gray-400">Connect your accounts to deploy your architecture</p>
                                                </div>
                                            </div>

                                            <CloudflareDeployWizard
                                                onStatusChange={setDeployStatus}
                                                disabled={isDeploying}
                                                architectureData={v2Data}
                                                sessionId={sessionId}
                                            />

                                            {/* Deploy Button */}
                                            <div className="mt-6 pt-6 border-t border-white/10">
                                                <Button
                                                    onClick={() => {
                                                        setIsDeploying(true);
                                                        // Navigate to deploy page with sessionId
                                                        navigate(`/deploy/${sessionId}`);
                                                    }}
                                                    disabled={!deployStatus?.ready || isDeploying}
                                                    className={`w-full py-4 rounded-xl font-semibold text-lg transition-all ${
                                                        deployStatus?.ready
                                                            ? 'bg-gradient-to-r from-[#F6821F] to-[#FAAD3F] text-white shadow-[0_0_30px_rgba(246,130,31,0.3)] hover:shadow-[0_0_40px_rgba(246,130,31,0.5)] transform hover:-translate-y-1'
                                                            : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                                                    }`}
                                                >
                                                    {isDeploying ? (
                                                        <span className="flex items-center justify-center gap-2">
                                                            <Icon name="spinner" size="sm" className="animate-spin" />
                                                            Deploying...
                                                        </span>
                                                    ) : deployStatus?.ready ? (
                                                        <span className="flex items-center justify-center gap-2">
                                                            <Icon name="rocket" size="sm" />
                                                            Deploy to Cloudflare
                                                        </span>
                                                    ) : (
                                                        <span className="flex items-center justify-center gap-2">
                                                            <Icon name="lock" size="sm" />
                                                            Connect accounts to deploy
                                                        </span>
                                                    )}
                                                </Button>
                                                {deployStatus?.ready && (
                                                    <p className="mt-3 text-xs text-center text-gray-500">
                                                        This will create a new repository and deploy to your Cloudflare account
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Action Buttons */}
                            <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
                                <Button
                                    onClick={() => navigate('/')}
                                    variant="primary"
                                    className="flex items-center justify-center bg-[#7B61FF] hover:bg-[#6b51eb] text-white px-8 py-3 rounded-xl shadow-[0_0_30px_rgba(123,97,255,0.3)] hover:shadow-[0_0_40px_rgba(123,97,255,0.5)] transition-all transform hover:-translate-y-1"
                                >
                                    <Icon name="rocket" size="xs" className="mr-2" />
                                    Start New Project
                                </Button>
                            </div>
                        </section>
                    )}
                </main>

                {/* Footer */}
                <footer className="border-t border-white/5 bg-black/40 backdrop-blur-md">
                    <div className="max-w-6xl mx-auto py-8 px-4 text-center">
                        <Text variant="muted" size="sm" className="text-gray-500">© 2025 The Architect. From vague ideas to precise micro-prompts.</Text>
                        <div className="mt-4 pt-4 border-t border-white/5">
                            <Text variant="muted" size="xs" className="text-gray-600">
                                This platform uses AI (Claude by Anthropic) to generate content.
                                AI outputs may contain errors and should be reviewed before use.
                            </Text>
                        </div>
                    </div>
                </footer>
            </div>

            {/* Floating Export Button */}
            {!isLoading && v2Data.analysis && (
                <ExportHTMLButton v2Data={v2Data} />
            )}

            {/* Mobile Bottom Tab Navigation - Glassmorphism */}
            {!isLoading && (v2Data.analysis || activeTab === 'questions') && (
                <MobileBottomNav items={[
                    { id: 'overview', icon: 'chart', label: 'Overview', onClick: () => handleTabSwitch('overview'), active: activeTab === 'overview' },
                    { id: 'research', icon: 'refresh', label: 'Evolution', onClick: () => handleTabSwitch('research'), active: activeTab === 'research' },
                    { id: 'technical', icon: 'settings', label: 'Technical', onClick: () => handleTabSwitch('technical'), active: activeTab === 'technical' },
                    { id: 'flowdiagram', icon: 'clipboard', label: 'Flow', onClick: () => handleTabSwitch('flowdiagram'), active: activeTab === 'flowdiagram' },
                    { id: 'prompts', icon: 'rocket', label: 'Prompts', onClick: () => handleTabSwitch('prompts'), active: activeTab === 'prompts' },
                    ...(v2Data.platform === 'cloudflare' ? [{ id: 'deploy', icon: 'lightning', label: 'Deploy', onClick: () => handleTabSwitch('deploy'), active: activeTab === 'deploy' }] : [])
                ]} />
            )}
        </div>
    );
} 